import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { RichTextEditor } from "components/DesignSystem/RichTextEditor/RichTextEditor";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useCreateOrUpdateInvoiceSettingsMutation } from "store/apis/invoices";
import { InvoiceSettingsType } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import {
  PAYMENT_INSTRUCTIONS_TEMPLATE,
  TEMPLATE_HELPER_TEXT,
} from "constants/invoicing";

type PaymentInstructionsModalProps = {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  invoiceSetting: InvoiceSettingsType;
};

type PaymentInstructionsValues = {
  payment_info: string;
};

export const PaymentInstructionsModal = ({
  isOpen,
  close,
  entityId,
  invoiceSetting,
}: PaymentInstructionsModalProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const [createInvoiceSetting, { isLoading }] =
    useCreateOrUpdateInvoiceSettingsMutation();

  const initialValues = {
    payment_info: invoiceSetting.payment_info || PAYMENT_INSTRUCTIONS_TEMPLATE,
  };

  const handleSubmit = async (values: PaymentInstructionsValues) => {
    const { address, ...settings } = invoiceSetting;

    try {
      await createInvoiceSetting({
        groupId,
        entityId,
        payload: { ...settings, payment_info: values.payment_info },
      }).unwrap();
      successToast({ message: "Payment instructions added" });
      close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="regular">
        <FormikForm<PaymentInstructionsValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <Modal.Header>
            <Modal.Title>Payment Instructions</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-flex t-flex-col t-gap-4 sm:t-px-4">
            <p className="t-m-0 t-text-body t-text-text-30">
              Once enabled, payment Instructions will be added by default for
              all the invoices and emails.
            </p>
            <div className="t-flex t-flex-col t-gap-1">
              <RichTextEditor
                name="payment_info"
                label="Enter payment instructions"
              />
              <span className="t-text-body-sm-italic t-text-text-30">
                {TEMPLATE_HELPER_TEXT}
              </span>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button">Cancel</Button>
            </Modal.RawClose>
            <Button
              type="submit"
              customType="primary"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Save instructions
            </Button>
          </Modal.FooterButtonGroup>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
